import React, { useState, useEffect, useMemo } from 'react';
import {
  Trash2,
  DollarSign,
  PieChart,
  Percent,
  RefreshCw,
  Loader2,
  Search,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Building2,
  CheckCircle2,
  Clock,
  TrendingDown,
  TrendingUp,
  Zap,
  Target,
  AlertTriangle,
  User,
  ArrowRight,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import wasteTrackingService, { WASTE_CATEGORIES } from '../../../../services/wasteTrackingService';
import branchService from '../../../../services/branchService';
import { useAuth } from '../../../../context/AuthContext';
import { useWasteTracking } from '../../../../hooks/useWasteTracking';
import { P } from '@/config/permissionCodes';
import LogWasteModal from './LogWasteModal';

// ── Badge colors for waste categories ────────────────────────────
const WASTE_BADGE_COLORS = {
  spoilage: 'bg-red-100 text-red-700',
  over_preparation: 'bg-blue-100 text-blue-700',
  damage: 'bg-slate-100 text-slate-700',
  plate_waste: 'bg-violet-100 text-violet-700',
  theft_shrinkage: 'bg-rose-100 text-rose-700',
  other: 'bg-gray-100 text-gray-700',
};

const WASTE_BADGE_DOTS = {
  spoilage: 'bg-red-500',
  over_preparation: 'bg-blue-500',
  damage: 'bg-slate-500',
  plate_waste: 'bg-violet-500',
  theft_shrinkage: 'bg-rose-500',
  other: 'bg-gray-400',
};

const WASTE_LABELS = Object.fromEntries(WASTE_CATEGORIES.map((c) => [c.value, c.label]));

// ── Weekly waste budget (mock) ───────────────────────────────────
const WEEKLY_WASTE_TARGET = 5000;

// ── Summary Card ─────────────────────────────────────────────────
function SummaryCard({ icon: Icon, title, value, subtitle, color, iconBg, iconColor, trend }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <div className="flex items-center gap-2">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {trend && (
              <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                trend.direction === 'up' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
              }`}>
                {trend.direction === 'up' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                {trend.value}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon size={24} className={iconColor} />
        </div>
      </div>
    </div>
  );
}

function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view Waste Tracking</p>
      </div>
    </div>
  );
}


export default function WasteTrackingList() {
  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permissions ──────────────────────────────────────────────────────────────
  const canViewScreen = hasPermission(P.INVENTORY.WASTAGE.SCREEN);
  const canLogWaste   = hasPermission(P.INVENTORY.WASTAGE.LOG);

  if (!canViewScreen) return <AccessDenied />;

  const [activeTab, setActiveTab] = useState(canLogWaste ? 'log' : 'analytics');
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // ── Log Waste modal (popup) — mirrors the Create PO modal pattern ──────────
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logModalPrefill, setLogModalPrefill] = useState(null);

  // Analytics tab
  const [analyticsPeriod, setAnalyticsPeriod] = useState(7);
  const [historySearch, setHistorySearch] = useState('');
  const [historyCategory, setHistoryCategory] = useState('');
  const [historyFromDate, setHistoryFromDate] = useState('');
  const [historyToDate, setHistoryToDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  // Use the custom hook for data
  const { todaysWaste, stats, analytics, inventoryItems, wasteEntries, loading, error, refetch, logWaste } = useWasteTracking(selectedBranchId, analyticsPeriod);

  // ── Open the Log Waste modal, optionally pre-filled from a Quick Log card ──
  const openLogModal = (prefill = null) => {
    if (!canLogWaste) return;
    setLogModalPrefill(prefill);
    setLogModalOpen(true);
  };
  const closeLogModal = () => {
    setLogModalOpen(false);
    setLogModalPrefill(null);
  };

  // ── Submit waste (called once per row by the modal) ─────────────────────────
  // No toast here — LogWasteModal shows a single combined summary toast after
  // all rows in a submission have been attempted, instead of one per row.
  const handleLogWasteSubmit = async (formPayload) => {
    const payload = {
      ...formPayload,
      branchId: selectedBranchId || user?.branchId || 1,
    };
    await logWaste(payload);
  };

  // ── Quick log prefill ─────────────────────────────────────────
  const handleQuickLog = (item) => {
    openLogModal({ itemId: item.id, itemName: item.name, category: item.lastCategory || '' });
  };

  // ── Quick-log top items (most frequently wasted) ───────────────
  const quickLogItems = useMemo(() => {
  // Use last 7 days from wasteEntries
  const now = new Date();
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 6);
  const freq = {};
  (wasteEntries || []).forEach((e) => {
    const dateStr = e.createdAt || e.createdat;
    if (!dateStr) return;
    const entryDate = new Date(dateStr);
    if (entryDate >= weekAgo && entryDate <= now) {
      // Support both itemId/inventoryItemId and itemName
      let id = e.itemId || e.inventoryItemId;
      // If no id, try to match by itemName
      if (!id && e.itemName) {
        const found = (inventoryItems || []).find((i) => i.name === e.itemName);
        if (found) id = found.id;
      }
      if (!id) return;
      freq[id] = (freq[id] || 0) + 1;
    }
  });
  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id, wasteCount]) => {
      // Try to find by id, fallback to name
      let item = (inventoryItems || []).find((i) => Number(i.id) === Number(id));
      if (!item) {
        // Try to find by name if id is string
        const entry = (wasteEntries || []).find((e) => (e.itemId || e.inventoryItemId) == id || e.itemName == id);
        if (entry && entry.itemName) {
          item = (inventoryItems || []).find((i) => i.name === entry.itemName);
        }
      }
      if (!item) return null;
      // Find the most recent waste entry for this item to get lastCategory
      const lastEntry = (wasteEntries || [])
        .filter((e) => (e.itemId || e.inventoryItemId) === Number(id) || e.itemName === item.name)
        .sort((a, b) => new Date(b.createdAt || b.createdat) - new Date(a.createdAt || a.createdat))[0];
      let lastCategory = lastEntry?.wasteCategory || '';
      // Fallback to 'other' if not found
      if (!lastCategory || !(lastCategory in WASTE_BADGE_DOTS)) lastCategory = 'other';
      return { ...item, wasteCount, lastCategory };
    })
    .filter(Boolean);
}, [wasteEntries, inventoryItems]);

  // ── Filtered & sorted history ──────────────────────────────────
  const filteredHistory = useMemo(() => {
    let result = [...wasteEntries];
    if (historySearch) {
      const q = historySearch.toLowerCase();
      result = result.filter((e) => e.itemName.toLowerCase().includes(q) || e.loggedBy.toLowerCase().includes(q));
    }
    if (historyCategory) {
      result = result.filter((e) => e.wasteCategory === historyCategory);
    }
    if (historyFromDate) {
      result = result.filter((e) => e.createdAt >= historyFromDate);
    }
    if (historyToDate) {
      result = result.filter((e) => e.createdAt <= historyToDate + 'T23:59:59Z');
    }
    result.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      if (typeof aVal === 'number') return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
    return result;
  }, [wasteEntries, historySearch, historyCategory, historyFromDate, historyToDate, sortConfig]);

  // ── Today's totals ─────────────────────────────────────────────
  const todayTotals = useMemo(() => ({
    count: todaysWaste.length,
    cost: todaysWaste.reduce((sum, e) => sum + (e.costImpact || 0), 0),
    qty: todaysWaste.reduce((sum, e) => sum + (e.quantity || 0), 0),
  }), [todaysWaste]);

  // ── Weekly waste spending for target tracking ──────────────────
  const weeklyWasteSpend = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 6); // last 7 days including today
    return (wasteEntries || []).reduce((sum, e) => {
      const dateStr = e.createdAt || e.createdat;
      if (!dateStr) return sum;
      const entryDate = new Date(dateStr);
      if (entryDate >= weekAgo && entryDate <= now) {
        const cost = e.costImpact ?? e.costimpact ?? Math.round((e.unitCost ?? e.unitcost ?? 0) * (e.quantity ?? 0));
        return sum + cost;
      }
      return sum;
    }, 0);
  }, [wasteEntries]);

  // ── Sort handler ───────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown size={14} className="text-slate-400" />;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} className="text-teal-600" /> : <ChevronDown size={14} className="text-teal-600" />;
  };

  // ── Loading ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col h-full bg-slate-50 -m-6">
        <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-900">Waste Tracking</h1>
            <button disabled className="p-2 rounded-lg text-slate-500 opacity-50">
              <RefreshCw size={18} className="animate-spin" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 size={40} className="text-teal-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading waste data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 -m-6">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-slate-900">Waste Tracking</h1>
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
              {canLogWaste && (
              <button
                onClick={() => setActiveTab('log')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  activeTab === 'log' ? 'bg-white text-teal-700 shadow-sm border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Waste Log
              </button>
              )}
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  activeTab === 'analytics' ? 'bg-white text-teal-700 shadow-sm border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                History & Analytics
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {branches.length > 1 && (
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={selectedBranchId ?? ''}
                  onChange={(e) => setSelectedBranchId(e.target.value || null)}
                  className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}
            <button
              onClick={refetch}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            {/* Primary action — opens the Log Waste popup, same pattern as "Create PO" */}
            {canLogWaste && (
              <button
                onClick={() => openLogModal()}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
              >
                <Plus size={16} />
                Log Waste
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            icon={Trash2}
            title="Total Waste Entries"
            value={stats?.totalWasteCount || 0}
            subtitle="All time"
            color="text-red-600"
            iconColor="text-red-600"
            iconBg="bg-red-100"
          />
          <SummaryCard
            icon={DollarSign}
            title="Waste Cost"
           value={`₹${(stats?.wasteCost || 0).toLocaleString('en-IN')}`}
            subtitle="Total cost impact"
            color="text-amber-600"
            iconColor="text-amber-600"
            iconBg="bg-amber-100"
            trend={{ direction: 'up', value: '12%' }}
          />
          <SummaryCard
            icon={PieChart}
            title="Top Wasted Category"
            value={stats?.topCategory || 'N/A'}
            subtitle="Highest frequency"
            color="text-violet-600"
            iconColor="text-violet-600"
            iconBg="bg-violet-100"
          />
          <SummaryCard
            icon={Percent}
            title="Waste vs Revenue"
            value={`${stats?.wasteVsRevenuePercent || 0}%`}
            subtitle="Of total revenue"
            color="text-rose-600"
            iconColor="text-rose-600"
            iconBg="bg-rose-100"
            trend={{ direction: 'down', value: '0.3%' }}
          />
        </div>

        {activeTab === 'log' && canLogWaste ? (
          <WasteLogTab
            todaysWaste={todaysWaste}
            todayTotals={todayTotals}
            quickLogItems={quickLogItems}
            onQuickLog={handleQuickLog}
            onLogWasteClick={() => openLogModal()}
            weeklyWasteSpend={weeklyWasteSpend}
            analytics={analytics}
          />
        ) : (
          <AnalyticsTab
            analytics={analytics}
            period={analyticsPeriod}
            setPeriod={setAnalyticsPeriod}
            history={filteredHistory}
            search={historySearch}
            setSearch={setHistorySearch}
            category={historyCategory}
            setCategory={setHistoryCategory}
            fromDate={historyFromDate}
            setFromDate={setHistoryFromDate}
            toDate={historyToDate}
            setToDate={setHistoryToDate}
            onSort={handleSort}
            SortIcon={SortIcon}
            weeklyWasteSpend={weeklyWasteSpend}
            wasteEntries={wasteEntries}
          />
        )}
      </div>

      {/* Log Waste popup — same modal pattern as Create/Edit PO */}
      {logModalOpen && canLogWaste && (
        <LogWasteModal
          inventoryItems={inventoryItems}
          prefill={logModalPrefill}
          onClose={closeLogModal}
          onSubmit={handleLogWasteSubmit}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// WASTE LOG TAB — Quick Log grid + Weekly Target + Today's Timeline
// (the manual entry form now lives in the LogWasteModal popup)
// ══════════════════════════════════════════════════════════════════
function WasteLogTab({
  todaysWaste, todayTotals, quickLogItems, onQuickLog, onLogWasteClick,
  weeklyWasteSpend, analytics,
}) {
  return (
    <div className="space-y-5">
      {/* Quick Log + Weekly Target Row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Quick Log Cards */}
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-2.5">
            <Zap size={14} className="text-amber-500" />
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick Log — Frequently Wasted</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {quickLogItems.length > 0 ? (
              quickLogItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onQuickLog(item)}
                  className="group relative bg-white rounded-xl border border-slate-200 px-3.5 py-3 text-left transition-all hover:shadow-md hover:border-teal-300 hover:-translate-y-0.5"
                >
                  <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${WASTE_BADGE_DOTS[item.lastCategory] || 'bg-gray-400'}`} />
                    <span className="text-[10px] text-slate-400">{WASTE_LABELS[item.lastCategory] || ''}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-slate-400">{item.currentStock.toFixed(1)} {item.uomSymbol}</span>
                    <span className="text-[10px] font-medium text-red-500">{item.wasteCount}x wasted</span>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight size={12} className="text-teal-500" />
                  </div>
                </button>
              ))
            ) : analytics && analytics.topWastedItems && analytics.topWastedItems.length > 0 ? (
              analytics.topWastedItems.slice(0, 5).map((item, i) => (
                <div
                  key={item.name}
                  className="group relative bg-white rounded-xl border px-3.5 py-3 text-left flex flex-col justify-between transition-all hover:shadow-md hover:border-teal-300 hover:-translate-y-0.5 border-slate-200"
                  style={{ minHeight: 110 }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-5 h-5 rounded-md ${i < 3 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'} flex items-center justify-center text-[10px] font-bold`}>{i + 1}</span>
                    <span className="text-sm text-slate-700 font-medium truncate">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-[10px] text-slate-400">₹{item.cost.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                    <div
                      className={`${i === 0 ? 'bg-red-500' : i === 1 ? 'bg-red-400' : i === 2 ? 'bg-amber-400' : i === 3 ? 'bg-amber-300' : 'bg-slate-300'} h-2 rounded-full transition-all`}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-6">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-2">
                  <Zap size={20} className="text-amber-400" />
                </div>
                <p className="text-slate-900 font-medium mb-1 text-center">No frequent waste items yet</p>
                <p className="text-xs text-slate-500 text-center">Log a waste entry to see quick log suggestions here.</p>
              </div>
            )}
          </div>
        </div>

        {/* Weekly Waste Target */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target size={14} className="text-teal-600" />
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Weekly Target</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className={`text-2xl font-bold ${weeklyWasteSpend > WEEKLY_WASTE_TARGET ? 'text-red-600' : 'text-slate-900'}`}>
                ₹{weeklyWasteSpend.toLocaleString('en-IN')}
              </span>
              <span className="text-xs text-slate-400">/ ₹{WEEKLY_WASTE_TARGET.toLocaleString('en-IN')}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  weeklyWasteSpend / WEEKLY_WASTE_TARGET > 0.9 ? 'bg-red-500' :
                  weeklyWasteSpend / WEEKLY_WASTE_TARGET > 0.7 ? 'bg-amber-500' : 'bg-teal-500'
                }`}
                style={{ width: `${Math.min(100, (weeklyWasteSpend / WEEKLY_WASTE_TARGET) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-500">
              {weeklyWasteSpend > WEEKLY_WASTE_TARGET ? (
                <span className="text-red-600 font-medium">Over budget by ₹{(weeklyWasteSpend - WEEKLY_WASTE_TARGET).toLocaleString('en-IN')}</span>
              ) : (
                <span>₹{(WEEKLY_WASTE_TARGET - weeklyWasteSpend).toLocaleString('en-IN')} remaining</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Today's Waste Timeline — now full width; the form moved to the popup */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-slate-900">Today's Waste</h2>
              <span className="text-xs text-slate-500">
                {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                {todayTotals.count} entries
              </span>
              <button
                onClick={onLogWasteClick}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
              >
                <Plus size={12} /> Log Waste
              </button>
            </div>
          </div>
          {todayTotals.cost > 0 && (
            <div className="mt-3 rounded-lg bg-gradient-to-r from-red-50 to-amber-50 border border-red-100 px-4 py-2.5 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Today's Waste Cost</span>
              <span className="text-lg font-bold text-red-600">₹{todayTotals.cost.toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>

        {todaysWaste.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={24} className="text-green-600" />
            </div>
            <p className="text-slate-900 font-medium mb-1">No waste today</p>
            <p className="text-sm text-slate-500 mb-4">Log a waste entry to start tracking today's totals</p>
            <button
              onClick={onLogWasteClick}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus size={14} /> Log Waste
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-200" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                {todaysWaste.map((entry, idx) => {
                  // Support both camelCase and snake_case/flat backend keys
                  const cost = entry.costImpact ?? entry.costimpact ?? Math.round((entry.unitCost ?? entry.unitcost ?? 0) * (entry.quantity ?? 0));
                  let dateObj;
                  if (entry.createdAt) dateObj = new Date(entry.createdAt);
                  else if (entry.createdat) dateObj = new Date(entry.createdat);
                  else dateObj = null;
                  const time = dateObj ? dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--:--';
                  return (
                    <div key={entry.id} className="relative flex gap-4 group">
                      {/* Timeline dot */}
                      <div className="relative z-10 flex flex-col items-center pt-1">
                        <div className={`w-[10px] h-[10px] rounded-full border-2 border-white ${WASTE_BADGE_DOTS[entry.wasteCategory] || 'bg-gray-400'} ring-2 ring-slate-100`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="bg-slate-50 rounded-lg px-4 py-3 hover:bg-slate-100/80 transition-colors group-hover:shadow-sm">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900 text-sm">{entry.itemName}</span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${WASTE_BADGE_COLORS[entry.wasteCategory] || 'bg-gray-100 text-gray-700'}`}>
                                  {WASTE_LABELS[entry.wasteCategory] || entry.wasteCategory}
                                </span>
                              </div>
                              {entry.reason && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-1">{entry.reason}</p>
                              )}
                            </div>
                            <span className="text-sm font-bold text-red-600 whitespace-nowrap ml-3">
                              ₹{cost.toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock size={10} />
                              {time}
                            </span>
                            <span className="flex items-center gap-1">
                              <User size={10} />
                              {entry.loggedBy}
                            </span>
                            <span>{entry.quantity.toFixed(2)} {entry.uom}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ANALYTICS TAB
// ══════════════════════════════════════════════════════════════════
function AnalyticsTab({ analytics, period, setPeriod, history, search, setSearch, category, setCategory, fromDate, setFromDate, toDate, setToDate, onSort, SortIcon, weeklyWasteSpend, wasteEntries }) {
  // ── Insight calculations ─────────────────────────────────────
  const insights = useMemo(() => {
    const now = new Date('2026-03-05');
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(thisWeekStart);
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

    const thisWeekStr = thisWeekStart.toISOString().split('T')[0];
    const lastWeekStartStr = lastWeekStart.toISOString().split('T')[0];
    const lastWeekEndStr = lastWeekEnd.toISOString().split('T')[0] + 'T23:59:59Z';

    const thisWeekEntries = wasteEntries.filter((e) => e.createdAt >= thisWeekStr);
    const lastWeekEntries = wasteEntries.filter((e) => e.createdAt >= lastWeekStartStr && e.createdAt <= lastWeekEndStr);

    const thisWeekCost = thisWeekEntries.reduce((s, e) => s + (e.costImpact || 0), 0);
    const lastWeekCost = lastWeekEntries.reduce((s, e) => s + (e.costImpact || 0), 0);
    const weekChange = lastWeekCost > 0 ? Math.round(((thisWeekCost - lastWeekCost) / lastWeekCost) * 100) : 0;

    // Top offender this week
    const itemCosts = {};
    thisWeekEntries.forEach((e) => {
      itemCosts[e.itemName] = (itemCosts[e.itemName] || 0) + (e.costImpact || 0);
    });
    const topOffender = Object.entries(itemCosts).sort((a, b) => b[1] - a[1])[0];

    // Top category
    const catCosts = {};
    thisWeekEntries.forEach((e) => {
      catCosts[e.wasteCategory] = (catCosts[e.wasteCategory] || 0) + (e.costImpact || 0);
    });
    const topCat = Object.entries(catCosts).sort((a, b) => b[1] - a[1])[0];
    const topCatLabel = topCat ? (WASTE_LABELS[topCat[0]] || topCat[0]) : 'N/A';

    return {
      thisWeekCost,
      lastWeekCost,
      weekChange,
      topOffender: topOffender ? { name: topOffender[0], cost: topOffender[1] } : null,
      topCat: topCat ? { label: topCatLabel, cost: topCat[1] } : null,
      weeklyTargetPct: Math.round((weeklyWasteSpend / WEEKLY_WASTE_TARGET) * 100),
    };
  }, [wasteEntries, weeklyWasteSpend]);

  return (
    <div className="space-y-5">
      {/* Insight Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Week over Week */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            {insights.weekChange > 0 ? (
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendingUp size={16} className="text-red-600" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingDown size={16} className="text-green-600" />
              </div>
            )}
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">This Week vs Last</p>
              <p className={`text-sm font-bold ${insights.weekChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {insights.weekChange > 0 ? '+' : ''}{insights.weekChange}% waste cost
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>₹{insights.thisWeekCost.toLocaleString('en-IN')}</span>
            <span className="text-slate-300">vs</span>
            <span>₹{insights.lastWeekCost.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {/* Top Offender */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={16} className="text-amber-600" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Top Offender</p>
              <p className="text-sm font-bold text-slate-900">
                {insights.topOffender?.name || 'N/A'}
              </p>
            </div>
          </div>
          {insights.topOffender && (
            <p className="text-xs text-slate-500">
              ₹{insights.topOffender.cost.toLocaleString('en-IN')} wasted this week
            </p>
          )}
          {insights.topCat && (
            <p className="text-[10px] text-slate-400 mt-1">
              Focus: reduce {insights.topCat.label.toLowerCase()} waste
            </p>
          )}
        </div>

        {/* Weekly Target Progress */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${insights.weeklyTargetPct > 90 ? 'bg-red-100' : 'bg-teal-100'}`}>
              <Target size={16} className={insights.weeklyTargetPct > 90 ? 'text-red-600' : 'text-teal-600'} />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Weekly Budget</p>
              <p className="text-sm font-bold text-slate-900">{insights.weeklyTargetPct}% used</p>
            </div>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mt-1">
            <div
              className={`h-2 rounded-full transition-all ${
                insights.weeklyTargetPct > 90 ? 'bg-red-500' : insights.weeklyTargetPct > 70 ? 'bg-amber-500' : 'bg-teal-500'
              }`}
              style={{ width: `${Math.min(100, insights.weeklyTargetPct)}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-1.5">
            ₹{weeklyWasteSpend.toLocaleString('en-IN')} / ₹{WEEKLY_WASTE_TARGET.toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <DailyWasteChart data={analytics.dailyTrend} period={period} setPeriod={setPeriod} />
          <TopWastedItems items={analytics.topWastedItems} />
          <WasteByCategoryChart categories={analytics.wasteByCategory} />
        </div>
      )}

      {/* History Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by item or user..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">All Categories</option>
            {WASTE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
          <span className="text-slate-400 text-sm">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer select-none" onClick={() => onSort('createdAt')}>
                  <div className="flex items-center gap-1">Date <SortIcon column="createdAt" /></div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer select-none" onClick={() => onSort('itemName')}>
                  <div className="flex items-center gap-1">Item <SortIcon column="itemName" /></div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Qty</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">UOM</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase cursor-pointer select-none" onClick={() => onSort('costImpact')}>
                  <div className="flex items-center gap-1 justify-end">Cost Impact <SortIcon column="costImpact" /></div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Waste Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Logged By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                        <CheckCircle2 size={24} className="text-slate-400" />
                      </div>
                      <p className="text-slate-900 font-medium mb-1">No entries found</p>
                      <p className="text-sm text-slate-500">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                history.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {new Date(entry.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(entry.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">{entry.itemName}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-slate-600">{entry.quantity.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-slate-500">{entry.uom}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-red-600">₹{(entry.costImpact || 0).toLocaleString('en-IN')}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${WASTE_BADGE_COLORS[entry.wasteCategory] || 'bg-gray-100 text-gray-700'}`}>
                        {WASTE_LABELS[entry.wasteCategory] || entry.wasteCategory}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 max-w-[200px] truncate block">{entry.reason || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                          <User size={10} className="text-slate-500" />
                        </div>
                        <span className="text-sm text-slate-600">{entry.loggedBy}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Daily Waste Cost Chart (CSS bars) ────────────────────────────
function DailyWasteChart({ data, period, setPeriod }) {
  const displayData = period === 7 ? data.slice(-7) : data;
  const maxCost = Math.max(...displayData.map((d) => d.cost), 1);
  const avgCost = Math.round(displayData.reduce((s, d) => s + d.cost, 0) / displayData.length);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Daily Waste Cost</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">Avg: ₹{avgCost.toLocaleString('en-IN')}/day</p>
        </div>
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
          <button
            onClick={() => setPeriod(7)}
            className={`px-2 py-1 text-xs font-medium rounded ${period === 7 ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}
          >
            7d
          </button>
          <button
            onClick={() => setPeriod(30)}
            className={`px-2 py-1 text-xs font-medium rounded ${period === 30 ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}
          >
            30d
          </button>
        </div>
      </div>
      <div className="relative">
        {/* Average line */}
        <div className="absolute left-0 right-0 border-t border-dashed border-slate-300"
          style={{ bottom: `${(avgCost / maxCost) * 100}%` }}
        >
          <span className="absolute -top-3 right-0 text-[8px] text-slate-400">avg</span>
        </div>
        <div className="flex items-end gap-1 h-32">
          {displayData.map((d, i) => {
            const heightPct = (d.cost / maxCost) * 100;
            const isAboveAvg = d.cost > avgCost;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative" title={`${d.label}: ₹${d.cost.toLocaleString('en-IN')}`}>
                <div className="w-full flex flex-col justify-end h-24">
                  <div
                    className={`w-full rounded-t-sm min-h-[2px] transition-all group-hover:opacity-80 ${isAboveAvg ? 'bg-red-400' : 'bg-teal-400'}`}
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
                {period === 7 && <span className="text-[10px] text-slate-400">{d.label.split(' ')[0]}</span>}
                {/* Tooltip */}
                <div className="absolute bottom-full mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                  ₹{d.cost.toLocaleString('en-IN')}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>{displayData[0]?.label}</span>
        <span>{displayData[displayData.length - 1]?.label}</span>
      </div>
    </div>
  );
}

// ── Top 5 Wasted Items (Horizontal bar ranking) ──────────────────
function TopWastedItems({ items }) {
  const maxCost = Math.max(...items.map((i) => i.cost), 1);
  const rankColors = ['bg-red-500', 'bg-red-400', 'bg-amber-400', 'bg-amber-300', 'bg-slate-300'];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-900 text-sm mb-4">Top 5 Wasted Items</h3>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-md ${i < 3 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'} flex items-center justify-center text-[10px] font-bold`}>
                  {i + 1}
                </span>
                <span className="text-sm text-slate-700 font-medium">{item.name}</span>
              </div>
              <span className="text-xs text-red-600 font-semibold">₹{item.cost.toLocaleString('en-IN')}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 ml-7">
              <div
                className={`${rankColors[i] || 'bg-slate-300'} h-2 rounded-full transition-all`}
                style={{ width: `${(item.cost / maxCost) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Waste by Category (Stacked bar + legend) ─────────────────────
function WasteByCategoryChart({ categories }) {
  const defaultColors = { bg: 'bg-gray-300', text: 'text-gray-600', light: 'bg-gray-100' };
  const totalCost = categories.reduce((sum, c) => sum + c.cost, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900 text-sm">Waste by Category</h3>
        <span className="text-xs text-slate-400">Total: ₹{totalCost.toLocaleString('en-IN')}</span>
      </div>

      {/* Stacked horizontal bar */}
      <div className="flex w-full h-6 rounded-lg overflow-hidden mb-4">
        {categories.map((cat) => {
          const colors = cat.colors && cat.colors.bg ? cat.colors : defaultColors;
          return (
            <div
              key={cat.key}
              className={`${colors.bg} transition-all hover:opacity-80 relative group`}
              style={{ width: `${cat.percent}%` }}
            >
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                {cat.label}: {cat.percent}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {categories.map((cat) => {
          const colors = cat.colors && cat.colors.bg ? cat.colors : defaultColors;
          return (
            <div key={cat.key} className="flex items-center justify-between group hover:bg-slate-50 rounded px-1 py-0.5 -mx-1 transition-colors">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-sm ${colors.bg}`} />
                <span className="text-xs text-slate-600">{cat.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">₹{cat.cost.toLocaleString('en-IN')}</span>
                <span className="text-xs font-semibold text-slate-700 w-8 text-right">{cat.percent}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}